import { Location } from '@angular/common';
import { HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ConfigurationService, RwcInterceptor } from '@mobi/rwc-utils-common-ng-jslib';

import { ServiceDiscoveryService } from './service-discovery/service-discovery.service';

export const COMMUNICATION_RELATION_HEADER = 'x-communication-relation';
/**
 * Intercepts requests and updates the request url such that requests are targeted
 * to the appropriate api gateway in the azure cloud.
 * You should only use this interceptor for SPAs deployed in the Azure cloud.
 *
 * ...
 * providers: [
 *     configureDefaultInterceptors({ useApiGatewayRoutingInterceptor: true })
 * ]
 * @endexample
 */
@Injectable({ providedIn: 'root' })
export class ApiGatewayRoutingInterceptor implements RwcInterceptor {
    #serviceDiscoveryService = inject(ServiceDiscoveryService);
    #configurationService = inject(ConfigurationService);

    appliesToRequest(req: HttpRequest<unknown>): boolean {
        // accept only relative urls which should not go to the rwc server
        return this.isRelativeUrl(req.url) && !req.url.match(`^/assets(/|\\?)`);
    }

    interceptRequest(req: HttpRequest<unknown>): Observable<HttpRequest<unknown>> {
        return this.#serviceDiscoveryService.resolve(req.url).pipe(
            map(serviceLocation => {
                const communicationRelation = this.getCommunicationRelation();
                const headersToUpdate = communicationRelation
                    ? { headers: req.headers.set(COMMUNICATION_RELATION_HEADER, communicationRelation) }
                    : {};

                let updatedRequest = req.clone({
                    url: Location.joinWithSlash(serviceLocation.baseUrl, serviceLocation.path),
                    ...headersToUpdate
                });
                if (serviceLocation.versionHeader) {
                    updatedRequest = this.updateHeader(
                        updatedRequest,
                        serviceLocation.versionHeader.name,
                        serviceLocation.versionHeader.value
                    );
                }
                return updatedRequest;
            })
        );
    }

    private getCommunicationRelation(): string | undefined {
        const infraPreset = this.#configurationService.getSync('infraPreset');
        if (infraPreset && ['b2b', 'b2c', 'b2e', 'b2eadmin'].includes(infraPreset)) {
            return infraPreset;
        }
        return undefined;
    }

    private updateHeader(updatedRequest: HttpRequest<unknown>, headerName: string, headerValue: string) {
        return updatedRequest.clone({
            headers: updatedRequest.headers.set(headerName, headerValue)
        });
    }

    private isRelativeUrl(url: string): boolean {
        return !url.toLowerCase().startsWith('https://') && !url.toLowerCase().startsWith('http://');
    }
}
